import { useState, useEffect } from "react"
import { useAuth } from "@/hooks/useAuth"
import { supabase } from "@/integrations/supabase/client"
import { useToast } from "@/hooks/use-toast"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { Switch } from "@/components/ui/switch"
import { Textarea } from "@/components/ui/textarea"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger, 
  DialogFooter 
} from "@/components/ui/dialog"
import { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle, 
  AlertDialogTrigger 
} from "@/components/ui/alert-dialog"
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select"
import { Gift, Plus, Edit, Trash2, Star, Trophy, Heart, Upload, Target, Award, Crown, Sparkles, Settings, Image } from "lucide-react"
import { DashboardLayout } from "@/components/layout/DashboardLayout"

interface Reward {
  id: string
  tenant_id: string
  name: string
  description: string
  reward_type: 'discount' | 'free_item' | 'points' | 'special'
  reward_value: string
  is_active: boolean
  created_at: string
  updated_at: string
  usage_count: number
  image_url?: string
}

interface ReferralRewardAssignment {
  id: string
  tenant_id: string
  reward_id: string
  reward?: Reward
  referral_count: number
  is_active: boolean
  created_at: string
  updated_at: string
}

interface RewardTemplate {
  id: string
  name: string
  description: string
  reward_type: string
  reward_value: string
  image_url?: string
  is_active: boolean
}

export default function RewardsManager() {
  const { user, loading: authLoading } = useAuth()
  const { toast } = useToast()
  const [rewards, setRewards] = useState<Reward[]>([])
  const [assignments, setAssignments] = useState<ReferralRewardAssignment[]>([])
  const [templates, setTemplates] = useState<RewardTemplate[]>([])
  const [loading, setLoading] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false)
  const [editingReward, setEditingReward] = useState<Reward | null>(null)
  const [selectedImage, setSelectedImage] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [maxReferralPosition, setMaxReferralPosition] = useState(20)
  const [settingsLoading, setSettingsLoading] = useState(false)
  const [selectedReferralCount, setSelectedReferralCount] = useState<number>(1)
  const [selectedRewardId, setSelectedRewardId] = useState<string>("")
  const [deleteAssignmentId, setDeleteAssignmentId] = useState<string | null>(null)
  const [formData, setFormData] = useState<{
    name: string
    description: string
    reward_type: 'discount' | 'free_item' | 'points' | 'special'
    reward_value: string
    is_active: boolean
  }>({
    name: "",
    description: "",
    reward_type: "discount",
    reward_value: "",
    is_active: true
  })

  useEffect(() => {
    if (!authLoading) {
      fetchData()
    }
  }, [authLoading])

  const fetchData = async () => {
    if (!user?.id) {
      setLoading(false)
      return
    }

    try {
      // Get user's tenant ID first
      const { data: tenantData, error: tenantError } = await supabase
        .from('tenants')
        .select('id, settings')
        .eq('admin_user_id', user.id)
        .maybeSingle()

      if (tenantError) throw tenantError
      
      if (!tenantData) {
        toast({
          title: "Kein Unternehmen",
          description: "Sie haben noch kein Unternehmen zugewiesen. Kontaktieren Sie einen Superladmin.",
          variant: "destructive",
        })
        setLoading(false)
        return
      }

      const tenantId = tenantData.id
      
      // Load maxReferralPosition from tenant settings
      const settings = tenantData.settings as any
      if (settings?.max_referral_position) {
        setMaxReferralPosition(settings.max_referral_position)
      }

      // Fetch rewards sorted by creation date
      const { data: rewardsData, error: rewardsError } = await supabase
        .from('rewards')
        .select('*')
        .eq('tenant_id', tenantId)
        .order('created_at', { ascending: false })

      if (rewardsError) throw rewardsError
      setRewards((rewardsData || []) as Reward[])

      // Fetch referral reward assignments
      const { data: assignmentsData, error: assignmentsError } = await supabase
        .from('referral_reward_assignments')
        .select('*')
        .eq('tenant_id', tenantId)
        .eq('is_active', true)
        .order('referral_count', { ascending: true })

      if (assignmentsError) throw assignmentsError

      // Manually join rewards data
      const assignmentsWithRewards = (assignmentsData || []).map(assignment => {
        const reward = rewardsData?.find(r => r.id === assignment.reward_id)
        return { ...assignment, reward: reward as Reward }
      })

      setAssignments(assignmentsWithRewards)

      // Fetch reward templates (for admins)
      const { data: templatesData, error: templatesError } = await supabase
        .from('reward_templates')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false })

      if (templatesError) {
        console.log('Templates not available or user is not admin')
      } else {
        setTemplates((templatesData || []) as RewardTemplate[])
      }

    } catch (error) {
      console.error('Error fetching data:', error)
      toast({
        title: "Fehler",
        description: "Daten konnten nicht geladen werden.",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleImageUpload = async (file: File): Promise<string | null> => {
    if (!user?.id) return null

    try {
      setUploading(true)
      
      const fileExt = file.name.split('.').pop()
      const fileName = `${user.id}/${Date.now()}.${fileExt}`
      
      const { data, error } = await supabase.storage
        .from('reward-images')
        .upload(fileName, file)

      if (error) throw error

      const { data: urlData } = supabase.storage
        .from('reward-images')
        .getPublicUrl(data.path)

      return urlData.publicUrl
    } catch (error) {
      console.error('Error uploading image:', error)
      toast({
        title: "Fehler",
        description: "Bild konnte nicht hochgeladen werden.",
        variant: "destructive",
      })
      return null
    } finally {
      setUploading(false)
    }
  }

  const handleSaveReward = async () => {
    if (!user?.id) return

    try {
      // Get user's tenant ID
      const { data: tenantData, error: tenantError } = await supabase
        .from('tenants')
        .select('id')
        .eq('admin_user_id', user.id)
        .maybeSingle()

      if (tenantError) throw tenantError
      if (!tenantData) {
        toast({
          title: "Fehler",
          description: "Kein Unternehmen gefunden.",
          variant: "destructive",
        })
        return
      }

      const tenantId = tenantData.id
      let imageUrl = editingReward?.image_url || null

      // Upload image if selected
      if (selectedImage) {
        imageUrl = await handleImageUpload(selectedImage)
      }

      const rewardData = {
        name: formData.name,
        description: formData.description,
        reward_type: formData.reward_type,
        reward_value: formData.reward_value,
        is_active: formData.is_active,
        image_url: imageUrl,
      }

      if (editingReward) {
        // Update existing reward
        const { error } = await supabase
          .from('rewards')
          .update(rewardData)
          .eq('id', editingReward.id)
          .eq('tenant_id', tenantId)

        if (error) throw error
        
        toast({
          title: "Erfolg",
          description: "Belohnung wurde aktualisiert.",
        })
      } else {
        // Create new reward
        const { error } = await supabase
          .from('rewards')
          .insert({
            ...rewardData,
            tenant_id: tenantId,
          })

        if (error) throw error
        
        toast({
          title: "Erfolg",
          description: "Neue Belohnung wurde erstellt.",
        })
      }

      await fetchData()
      resetForm()
      setDialogOpen(false)
    } catch (error) {
      console.error('Error saving reward:', error)
      toast({
        title: "Fehler",
        description: "Belohnung konnte nicht gespeichert werden.",
        variant: "destructive",
      })
    }
  }

  const handleDeleteReward = async (rewardId: string) => {
    try {
      const { error } = await supabase
        .from('rewards')
        .delete()
        .eq('id', rewardId)

      if (error) throw error

      await fetchData()
      toast({
        title: "Erfolg",
        description: "Belohnung wurde gelöscht.",
      })
    } catch (error) {
      console.error('Error deleting reward:', error)
      toast({
        title: "Fehler",
        description: "Belohnung konnte nicht gelöscht werden.",
        variant: "destructive",
      })
    }
  }

  const handleToggleActive = async (rewardId: string) => {
    try {
      const reward = rewards.find(r => r.id === rewardId)
      if (!reward) return

      const { error } = await supabase
        .from('rewards')
        .update({ is_active: !reward.is_active })
        .eq('id', rewardId)

      if (error) throw error

      await fetchData()
      toast({
        title: "Erfolg",
        description: "Belohnungsstatus wurde geändert.",
      })
    } catch (error) {
      console.error('Error toggling reward status:', error)
      toast({
        title: "Fehler",
        description: "Status konnte nicht geändert werden.",
        variant: "destructive",
      })
    }
  }

  const handleAssignReward = async () => {
    if (!user?.id || !selectedRewardId || !selectedReferralCount) return

    try {
      const { data: tenantData, error: tenantError } = await supabase
        .from('tenants')
        .select('id')
        .eq('admin_user_id', user.id)
        .maybeSingle()

      if (tenantError || !tenantData) throw new Error('Tenant not found')

      // Check if assignment already exists for this referral count
      const existingAssignment = assignments.find(a => a.referral_count === selectedReferralCount)
      
      if (existingAssignment) {
        // Update existing assignment
        const { error } = await supabase
          .from('referral_reward_assignments')
          .update({ reward_id: selectedRewardId })
          .eq('id', existingAssignment.id)

        if (error) throw error
      } else {
        // Create new assignment
        const { error } = await supabase
          .from('referral_reward_assignments')
          .insert({
            tenant_id: tenantData.id,
            reward_id: selectedRewardId,
            referral_count: selectedReferralCount,
            is_active: true
          })

        if (error) throw error
      }

      toast({
        title: "Erfolg",
        description: `Prämie erfolgreich ${existingAssignment ? 'aktualisiert' : 'zugewiesen'}.`,
      })

      setAssignmentDialogOpen(false)
      setSelectedRewardId("")
      setSelectedReferralCount(1)
      await fetchData()

    } catch (error) {
      console.error('Error assigning reward:', error)
      toast({
        title: "Fehler",
        description: "Prämie konnte nicht zugewiesen werden.",
        variant: "destructive",
      })
    }
  }

  const handleDeleteAssignment = async (assignmentId: string) => {
    try {
      const { error } = await supabase
        .from('referral_reward_assignments')
        .delete()
        .eq('id', assignmentId)

      if (error) throw error

      toast({
        title: "Erfolg",
        description: "Prämienzuweisung wurde entfernt.",
      })

      await fetchData()
    } catch (error) {
      console.error('Error deleting assignment:', error)
      toast({
        title: "Fehler",
        description: "Zuweisung konnte nicht entfernt werden.",
        variant: "destructive",
      })
    }
  }

  const createFromTemplate = (template: RewardTemplate) => {
    setFormData({
      name: template.name,
      description: template.description,
      reward_type: template.reward_type as any,
      reward_value: template.reward_value,
      is_active: true
    })
    // Set template image if available
    if (template.image_url) {
      setSelectedImage(null) // Clear any file, we'll use the URL directly
    }
    setDialogOpen(true)
  }

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      reward_type: "discount",
      reward_value: "",
      is_active: true
    })
    setEditingReward(null)
    setSelectedImage(null)
  }

  const openEditDialog = (reward: Reward) => {
    setEditingReward(reward)
    setFormData({
      name: reward.name,
      description: reward.description,
      reward_type: reward.reward_type,
      reward_value: reward.reward_value,
      is_active: reward.is_active
    })
    setDialogOpen(true)
  }

  const handleSaveMaxReferralPosition = async () => {
    if (!user?.id) return

    setSettingsLoading(true)
    try {
      // Get current tenant
      const { data: tenantData } = await supabase
        .from('tenants')
        .select('id, settings')
        .eq('admin_user_id', user.id)
        .single()

      if (tenantData) {
        const currentSettings = tenantData.settings as any || {}
        const updatedSettings = {
          ...currentSettings,
          max_referral_position: maxReferralPosition
        }

        const { error } = await supabase
          .from('tenants')
          .update({
            settings: updatedSettings,
            updated_at: new Date().toISOString()
          })
          .eq('id', tenantData.id)

        if (error) throw error

        toast({
          title: "Einstellungen gespeichert",
          description: `Maximale Empfehlungsposition wurde auf ${maxReferralPosition} gesetzt.`,
        })
      }
    } catch (error) {
      console.error('Error saving max referral position:', error)
      toast({
        variant: "destructive",
        title: "Fehler",
        description: "Die Einstellung konnte nicht gespeichert werden.",
      })
    } finally {
      setSettingsLoading(false)
    }
  }

  const getRewardIcon = (type: string) => {
    switch (type) {
      case 'discount': return <Star className="h-4 w-4" />
      case 'free_item': return <Gift className="h-4 w-4" />
      case 'points': return <Trophy className="h-4 w-4" />
      default: return <Heart className="h-4 w-4" />
    }
  }

  const getRewardTypeLabel = (type: string) => {
    switch (type) {
      case 'discount': return 'Rabatt'
      case 'free_item': return 'Gratis Artikel'
      case 'points': return 'Punkte'
      case 'special': return 'Spezial'
      default: return type
    }
  }

  const getRewardColor = (type: string) => {
    switch (type) {
      case 'discount': return 'from-orange-400 to-orange-600'
      case 'free_item': return 'from-blue-400 to-blue-600'
      case 'points': return 'from-green-400 to-green-600'
      case 'special': return 'from-purple-400 to-purple-600'
      default: return 'from-gray-400 to-gray-600'
    }
  }

  const renderProgressBar = () => {
    const positions = Array.from({ length: Math.min(maxReferralPosition, 100) }, (_, i) => i + 1)
    
    return (
      <div className="relative w-full h-32 bg-gradient-to-r from-primary/10 via-accent/10 to-success/10 rounded-lg border overflow-x-auto">
        <div className="absolute top-4 left-4 right-4 h-2 bg-gradient-to-r from-primary via-accent to-success rounded-full opacity-50" />
        
        <div className="flex items-center justify-between px-4 py-2 h-full overflow-x-auto">
          {positions.map((position) => {
            const assignment = assignments.find(a => a.referral_count === position)
            const hasReward = !!assignment
            
            return (
              <div
                key={position}
                className="flex flex-col items-center justify-center min-w-16 cursor-pointer group"
                onClick={() => {
                  setSelectedReferralCount(position)
                  setSelectedRewardId(assignment?.reward_id || "")
                  setAssignmentDialogOpen(true)
                }}
              >
                <div className="relative">
                  <div 
                    className={`w-12 h-12 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${
                      hasReward 
                        ? `bg-gradient-to-br ${getRewardColor(assignment.reward?.reward_type || '')} border-white shadow-lg text-white` 
                        : 'bg-white border-gray-300 hover:border-primary hover:shadow-md text-gray-400 hover:text-primary'
                    }`}
                  >
                    {hasReward && assignment.reward ? (
                      <div className={`p-2 rounded-full bg-gradient-to-br ${getRewardColor(assignment.reward.reward_type)} text-white`}>
                        {assignment.reward.reward_type === 'discount' ? <Award className="h-6 w-6" /> :
                         assignment.reward.reward_type === 'free_item' ? <Gift className="h-6 w-6" /> :
                         assignment.reward.reward_type === 'points' ? <Target className="h-6 w-6" /> :
                         assignment.reward.reward_type === 'special' ? <Crown className="h-6 w-6" /> :
                         <Sparkles className="h-6 w-6" />}
                      </div>
                    ) : (
                      <Plus className="h-4 w-4" />
                    )}
                  </div>
                  
                  {hasReward && (
                    <Button
                      size="sm"
                      variant="destructive"
                      className="absolute -top-2 -right-2 w-6 h-6 rounded-full p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={(e) => {
                        e.stopPropagation()
                        setDeleteAssignmentId(assignment.id)
                      }}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  )}
                </div>
                
                <Badge 
                  variant={hasReward ? "default" : "outline"} 
                  className="mt-2 text-xs"
                >
                  {position}
                </Badge>
                
                {hasReward && assignment.reward && (
                  <div className="text-xs text-center mt-1 max-w-16 truncate">
                    {assignment.reward.name}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="container mx-auto p-6 space-y-6">
          <h1 className="text-3xl font-bold">Prämien verwalten</h1>
          <div className="text-center py-8">
            <div className="text-muted-foreground">Lade Prämien...</div>
          </div>
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
      <div className="container mx-auto p-6 space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">Prämien verwalten</h1>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={resetForm}>
                <Plus className="h-4 w-4 mr-2" />
                Neue Belohnung
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[600px]">
              <DialogHeader>
                <DialogTitle>
                  {editingReward ? 'Belohnung bearbeiten' : 'Neue Belohnung erstellen'}
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name der Belohnung</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="z.B. 10% Rabatt"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Beschreibung</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Beschreiben Sie die Belohnung..."
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="type">Belohnungstyp</Label>
                    <Select value={formData.reward_type} onValueChange={(value: any) => setFormData(prev => ({ ...prev, reward_type: value }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="discount">Rabatt</SelectItem>
                        <SelectItem value="free_item">Gratis Artikel</SelectItem>
                        <SelectItem value="points">Punkte</SelectItem>
                        <SelectItem value="special">Spezial</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="value">Wert der Belohnung</Label>
                    <Input
                      id="value"
                      value={formData.reward_value}
                      onChange={(e) => setFormData(prev => ({ ...prev, reward_value: e.target.value }))}
                      placeholder={formData.reward_type === 'discount' ? 'z.B. 10%' : 'z.B. Kaffee'}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="image">Bild (optional)</Label>
                  <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-6 text-center">
                    {editingReward?.image_url && !selectedImage ? (
                      <div className="space-y-2">
                        <img 
                          src={editingReward.image_url} 
                          alt="Reward" 
                          className="w-24 h-24 object-cover rounded-lg mx-auto"
                        />
                        <Button
                          variant="outline"
                          onClick={() => {
                            const input = document.createElement('input')
                            input.type = 'file'
                            input.accept = 'image/*'
                            input.onchange = (e) => {
                              const file = (e.target as HTMLInputElement).files?.[0]
                              if (file) setSelectedImage(file)
                            }
                            input.click()
                          }}
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          Bild ändern
                        </Button>
                      </div>
                    ) : selectedImage ? (
                      <div className="space-y-2">
                        <img 
                          src={URL.createObjectURL(selectedImage)} 
                          alt="Preview" 
                          className="w-24 h-24 object-cover rounded-lg mx-auto"
                        />
                        <Button
                          variant="outline"
                          onClick={() => setSelectedImage(null)}
                        >
                          Entfernen
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <Image className="h-12 w-12 mx-auto text-muted-foreground" />
                        <div className="text-sm text-muted-foreground">
                          <Button
                            variant="outline"
                            onClick={() => {
                              const input = document.createElement('input')
                              input.type = 'file'
                              input.accept = 'image/*'
                              input.onchange = (e) => {
                                const file = (e.target as HTMLInputElement).files?.[0]
                                if (file) setSelectedImage(file)
                              }
                              input.click()
                            }}
                          >
                            <Upload className="h-4 w-4 mr-2" />
                            Bild hochladen
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                  />
                  <Label htmlFor="active">Belohnung ist aktiv</Label>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)}>
                  Abbrechen
                </Button>
                <Button onClick={handleSaveReward} disabled={uploading}>
                  {uploading ? 'Speichere...' : editingReward ? 'Aktualisieren' : 'Erstellen'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <Tabs defaultValue="rewards" className="space-y-6">
          <TabsList>
            <TabsTrigger value="rewards">Prämien-Verwaltung</TabsTrigger>
            <TabsTrigger value="referral-rewards">Empfehlungs-Prämien</TabsTrigger>
            {templates.length > 0 && (
              <TabsTrigger value="templates">Vorlagen</TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="rewards" className="space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="bg-primary/10 p-3 rounded-lg">
                      <Gift className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Gesamt Prämien</p>
                      <p className="text-2xl font-bold">{rewards.length}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="bg-green-500/10 p-3 rounded-lg">
                      <Star className="h-6 w-6 text-green-500" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Aktive Prämien</p>
                      <p className="text-2xl font-bold">
                        {rewards.filter(r => r.is_active).length}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="bg-blue-500/10 p-3 rounded-lg">
                      <Trophy className="h-6 w-6 text-blue-500" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Eingelöste Prämien</p>
                      <p className="text-2xl font-bold">
                        {rewards.reduce((sum, r) => sum + r.usage_count, 0)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="bg-orange-500/10 p-3 rounded-lg">
                      <Heart className="h-6 w-6 text-orange-500" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Belohnungstypen</p>
                      <p className="text-2xl font-bold">
                        {new Set(rewards.map(r => r.reward_type)).size}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Rewards List */}
            <Card>
              <CardHeader>
                <CardTitle>Alle Belohnungen</CardTitle>
              </CardHeader>
              <CardContent>
                {rewards.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Gift className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Keine Belohnungen erstellt.</p>
                    <p className="text-sm">Erstellen Sie Ihre erste Belohnung, um zu beginnen.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {rewards.map((reward) => (
                      <div
                        key={reward.id}
                        className="flex items-center gap-4 p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                      >
                        {reward.image_url ? (
                          <img 
                            src={reward.image_url} 
                            alt={reward.name}
                            className="w-12 h-12 object-cover rounded-lg"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center">
                            {getRewardIcon(reward.reward_type)}
                          </div>
                        )}
                        
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold">{reward.name}</h3>
                            <Badge variant="outline">
                              {getRewardTypeLabel(reward.reward_type)}
                            </Badge>
                            <Badge variant="secondary">{reward.reward_value}</Badge>
                            {reward.is_active ? (
                              <Badge className="bg-green-500">Aktiv</Badge>
                            ) : (
                              <Badge variant="secondary">Inaktiv</Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground mb-1">
                            {reward.description}
                          </p>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <span>Typ: {getRewardTypeLabel(reward.reward_type)}</span>
                            <span>Verwendet: {reward.usage_count}×</span>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEditDialog(reward)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleToggleActive(reward.id)}
                          >
                            <Switch checked={reward.is_active} />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="outline" size="sm">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Belohnung löschen</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Sind Sie sicher, dass Sie "{reward.name}" löschen möchten? 
                                  Diese Aktion kann nicht rückgängig gemacht werden.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleDeleteReward(reward.id)}>
                                  Löschen
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="referral-rewards" className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold">Empfehlungs-Prämien</h2>
                <p className="text-muted-foreground">
                  Belohnen Sie Ihre Unterstützer für erfolgreiche Empfehlungen
                </p>
              </div>
              
              <div className="flex gap-2">
                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="outline">
                      <Settings className="h-4 w-4 mr-2" />
                      Einstellungen
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Empfehlungs-Einstellungen</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="maxPosition">Maximale Empfehlungsposition</Label>
                        <Input
                          id="maxPosition"
                          type="number"
                          min="5"
                          max="100"
                          value={maxReferralPosition}
                          onChange={(e) => setMaxReferralPosition(Math.min(100, Math.max(5, parseInt(e.target.value) || 20)))}
                        />
                        <p className="text-sm text-muted-foreground mt-1">
                          Bestimmen Sie, wie viele Empfehlungsplätze angezeigt werden sollen (5-100)
                        </p>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button 
                        onClick={handleSaveMaxReferralPosition}
                        disabled={settingsLoading}
                      >
                        {settingsLoading ? "Speichere..." : "Speichern"}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                <Dialog open={assignmentDialogOpen} onOpenChange={setAssignmentDialogOpen}>
                  <DialogTrigger asChild>
                    <Button onClick={() => {
                      setSelectedReferralCount(1)
                      setSelectedRewardId("")
                    }}>
                      <Plus className="h-4 w-4 mr-2" />
                      Prämie zuweisen
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Prämie zuweisen</DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="referralCount">Empfehlungsanzahl</Label>
                        <Input
                          id="referralCount"
                          type="number"
                          min="1"
                          max="100"
                          value={selectedReferralCount}
                          onChange={(e) => setSelectedReferralCount(parseInt(e.target.value) || 1)}
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="reward">Prämie auswählen</Label>
                        <Select value={selectedRewardId} onValueChange={setSelectedRewardId}>
                          <SelectTrigger>
                            <SelectValue placeholder="Wählen Sie eine Prämie aus..." />
                          </SelectTrigger>
                          <SelectContent>
                            {rewards.filter(r => r.is_active).map((reward) => (
                              <SelectItem key={reward.id} value={reward.id}>
                                <div className="flex items-center gap-2">
                                  {getRewardIcon(reward.reward_type)}
                                  <span>{reward.name}</span>
                                  <Badge variant="outline">{reward.reward_value}</Badge>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <DialogFooter>
                      <Button variant="outline" onClick={() => setAssignmentDialogOpen(false)}>
                        Abbrechen
                      </Button>
                      <Button onClick={handleAssignReward} disabled={!selectedRewardId}>
                        Zuweisen
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </div>

            {/* Empfehlungs-Fortschrittsleiste */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5" />
                  Empfehlungs-Belohnungsleiste
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  Klicken Sie auf eine Position, um eine Prämie zuzuweisen oder zu bearbeiten
                </p>
              </CardHeader>
              <CardContent>
                {renderProgressBar()}
                
                <div className="mt-4 text-center text-sm text-muted-foreground">
                  <p>Ihre Unterstützer sehen nur die Positionen mit zugewiesenen Prämien</p>
                </div>
              </CardContent>
            </Card>

            {/* Verfügbare Prämien für Empfehlungen */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Gift className="h-5 w-5" />
                  Verfügbare Prämien ({rewards.filter(r => r.is_active).length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {rewards.filter(r => r.is_active).length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Gift className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Keine aktiven Prämien verfügbar.</p>
                    <p className="text-sm">Erstellen Sie zuerst aktive Prämien in der Prämien-Verwaltung.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {rewards.filter(r => r.is_active).map((reward) => (
                      <Card key={reward.id} className="hover:shadow-md transition-shadow cursor-pointer">
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            {reward.image_url ? (
                              <img 
                                src={reward.image_url} 
                                alt={reward.name}
                                className="w-12 h-12 object-cover rounded-lg"
                              />
                            ) : (
                              <div className={`p-3 rounded-lg bg-gradient-to-br ${getRewardColor(reward.reward_type)} text-white`}>
                                {reward.reward_type === 'discount' ? <Award className="h-6 w-6" /> :
                                 reward.reward_type === 'free_item' ? <Gift className="h-6 w-6" /> :
                                 reward.reward_type === 'points' ? <Target className="h-6 w-6" /> :
                                 reward.reward_type === 'special' ? <Crown className="h-6 w-6" /> :
                                 <Sparkles className="h-6 w-6" />}
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <h3 className="font-semibold truncate">{reward.name}</h3>
                              <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                                {reward.description}
                              </p>
                              <div className="flex items-center gap-2">
                                <Badge variant="secondary">{reward.reward_value}</Badge>
                                <Badge variant="outline" className="text-xs">
                                  {reward.usage_count}× verwendet
                                </Badge>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {templates.length > 0 && (
            <TabsContent value="templates" className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold">Prämien-Vorlagen</h2>
                <p className="text-muted-foreground">
                  Nutzen Sie diese vorgefertigten Vorlagen als Ausgangspunkt für Ihre Prämien
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {templates.map((template) => (
                  <Card key={template.id} className="hover:shadow-md transition-shadow cursor-pointer"
                        onClick={() => createFromTemplate(template)}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        {template.image_url ? (
                          <img 
                            src={template.image_url} 
                            alt={template.name}
                            className="w-12 h-12 object-cover rounded-lg"
                          />
                        ) : (
                          <div className={`p-3 rounded-lg bg-gradient-to-br ${getRewardColor(template.reward_type)} text-white`}>
                            {getRewardIcon(template.reward_type)}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold truncate">{template.name}</h3>
                          <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                            {template.description}
                          </p>
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary">{template.reward_value}</Badge>
                            <Badge variant="outline" className="text-xs">
                              {getRewardTypeLabel(template.reward_type)}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>
          )}
        </Tabs>

        {/* Delete Assignment Confirmation */}
        <AlertDialog open={!!deleteAssignmentId} onOpenChange={() => setDeleteAssignmentId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Prämienzuweisung entfernen</AlertDialogTitle>
              <AlertDialogDescription>
                Sind Sie sicher, dass Sie diese Prämienzuweisung entfernen möchten?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Abbrechen</AlertDialogCancel>
              <AlertDialogAction onClick={() => {
                if (deleteAssignmentId) {
                  handleDeleteAssignment(deleteAssignmentId)
                  setDeleteAssignmentId(null)
                }
              }}>
                Entfernen
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  )
}